import {
  Box,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tabs,
  Toolbar,
} from '@mui/material'
import React, {useEffect, useState} from 'react'
import QuestionRow from '../../../components/screens/surveyForms/questions/QuestionRow'
import {AUDIENCE_SELECT, ITEM_TYPE, QUESTION_TYPE} from '../../../utils/constants'
import MultipleOption from '../../../components/screens/surveyForms/questions/QuestionTypeComponents/MultipleOption'
import WbTabPanel from '../../../components/common/WbTabPanel'
import WbTab from '../../../components/common/WbTab'
import {Close, MoreHoriz} from '@mui/icons-material'
import surveyQuestionApi from '../../../services/api/surveys/surveyQuestionApi'
import WbAlert from '../../../components/common/WbAlert'
import TextQuestion from '../../../components/screens/surveyForms/questions/QuestionTypeComponents/TextQuestion'
import RankingQuestion from '../../../components/screens/surveyForms/questions/QuestionTypeComponents/RankingQuestion'
import NumberSlider from '../../../components/screens/surveyForms/questions/QuestionTypeComponents/NumberSlider'
import WbOutlineButton from '../../../components/common/WbOutlineButton'
import MultipleSelect from '../../../components/screens/surveyForms/questions/QuestionTypeComponents/MultipleSelect'
import questionsApi from '../../../services/api/questionLibrary/questionsApi'
import {useNavigate, useParams} from 'react-router-dom'
import paths from '../../../routes/paths'
import {surveysApi} from '../../../services'
import {questionIdForSave} from './cloneOnSave'
import surveyItemApi from '../../../services/api/surveys/surveyItemApi'
import {loadBuilderItems} from './loadBuilderItems'
import {
  isContentScreen,
  isSkipLogicSection,
  toEngineSurvey,
} from '../../../utils/surveyItems'
import {
  contentScreenTitle,
  normalizeContentScreen,
  validateContentScreen,
} from '../../../utils/contentScreen'
import {findReferences} from '../../../utils/skipLogic/references'
import {validateRules} from '../../../utils/skipLogic/rules'
import ContentScreenEditor from '../../../components/screens/surveyForms/items/ContentScreenEditor'
import SkipLogicSectionEditor from '../../../components/screens/surveyForms/items/SkipLogicSectionEditor'
import ItemDeleteGuard from '../../../components/screens/surveyForms/items/ItemDeleteGuard'

export default function QuestionsList({
  survey,
  questions,
  index,
  setIndex,
  onQuestionChange,
  priorityAudience,
}) {
  const [value, setValue] = useState(1)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [contentErrors, setContentErrors] = useState([])
  const [pendingDelete, setPendingDelete] = useState(null)
  const [skipErrors, setSkipErrors] = useState([])
  const open = Boolean(anchorEl)
  const token = useParams().token

  const navigate = useNavigate()

  const normalizeSurveyQuestions = rows =>
    (Array.isArray(rows) ? rows : []).map(q => {
      if (isContentScreen(q) || isSkipLogicSection(q)) return q
      const categoryId = q.categoryId ?? q.categoryID ?? q.CategoryId ?? null
      const categoryName =
        q.categoryName ??
        q.categoryname ??
        q.CategoryName ??
        q.category?.name ??
        null

      return {
        ...q,
        categoryId,
        categoryName,
        questionSettings: {...q.questionSettings},
        sliderResponseAttributes: {...q.sliderResponseAttributes},
        multiOptionResponseAttributes: q.multiOptionResponseAttributes
          ? [...q.multiOptionResponseAttributes]
          : null,
        followupQuestions: q.followupQuestions ? [...q.followupQuestions] : null,
        openResponseAttributes: {...q.openResponseAttributes},
      }
    })

  const refreshSurveyQuestionsByToken = async keepQuestionId => {
    const latestQuestions = await loadBuilderItems(survey?.id, token)
    const normalizedQuestions = normalizeSurveyQuestions(latestQuestions)
    onQuestionChange(normalizedQuestions)

    if (keepQuestionId) {
      const nextIndex = normalizedQuestions.findIndex(x => x.id === keepQuestionId)
      setIndex(nextIndex > -1 ? nextIndex : null)
    }
  }

  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

  const saveQuestionAtIndex = async (i, options = {}) => {
    const {showSuccessAlert = true, categoryOverride = null} = options
    const sourceQuestion = {
      ...questions[i],
      ...(categoryOverride || {}),
    }
    const isCommentQuestion = sourceQuestion.questionType === QUESTION_TYPE.COMMENTS
    const savedQuestion = await questionsApi.post({
      ...sourceQuestion,
      // Clone shared bank/template questions (isAdmin) into a survey-owned copy; update the
      // survey's own questions in place. Keeps the survey's TemplateId intact for benchmarking.
      // No survey context (SuperAdmin bank editor) => always edit in place, never clone.
      id: questionIdForSave(sourceQuestion, survey),
      categoryId: isCommentQuestion ? null : (sourceQuestion.categoryId ?? null),
      categoryName: isCommentQuestion ? null : (sourceQuestion.categoryName ?? null),
      questionSettings: {
        ...sourceQuestion.questionSettings,
      },
      followupQuestions: sourceQuestion.followupQuestions
        ? [...sourceQuestion.followupQuestions]
        : null,
      multiOptionResponseAttributes: sourceQuestion.multiOptionResponseAttributes
        ? [...sourceQuestion.multiOptionResponseAttributes]
        : null,
      openResponseAttributes: {
        ...sourceQuestion.openResponseAttributes,
      },
      sliderResponseAttributes: {
        ...sourceQuestion.sliderResponseAttributes,
      },
      tags: sourceQuestion.tags ? [...sourceQuestion.tags] : null,
    })

    if (survey && survey.id) {
      // Order is 1-based everywhere else (CopyTemplate seeds 1..N, onReorder writes i+1); `i`
      // here is the 0-based list index, so send i+1. Sending the raw 0-based index made an edited
      // question collide with the 1-based template orders (two questions at Order 1, a gap at 2).
      const order = i + 1
      // Ensure relation update completes before refetching questions.
      if (sourceQuestion.id === 0) {
        await surveyQuestionApi.post({
          surveyId: survey.id,
          questionId: savedQuestion.id,
          order,
        })
      } else {
        await surveyQuestionApi.addSurveyQuestionRemoveTempId(
          survey.id,
          sourceQuestion.id,
          savedQuestion.id,
          order
        )
      }
    }

    const savedQuestionId = savedQuestion?.id || sourceQuestion?.id
    let normalizedQuestions = []
    for (let attempt = 0; attempt < 3; attempt++) {
      const latestQuestions = await loadBuilderItems(survey?.id, token)
      normalizedQuestions = normalizeSurveyQuestions(latestQuestions)
      const savedRow = normalizedQuestions.find(x => x.id === savedQuestionId)
      const hasCategoryName =
        !isCommentQuestion &&
        !!(savedRow?.categoryName ?? savedRow?.category?.name ?? null)

      if (isCommentQuestion || hasCategoryName || attempt === 2) {
        break
      }
      await sleep(200)
    }

    onQuestionChange(normalizedQuestions)
    if (savedQuestionId) {
      const nextIndex = normalizedQuestions.findIndex(x => x.id === savedQuestionId)
      setIndex(nextIndex > -1 ? nextIndex : i)
    }

    if (showSuccessAlert) {
      WbAlert({
        message: 'Question saved successfully',
        type: 'success',
      })
    }
  }

  const saveQuestionWithStatusCheck = async (i, options = {}) => {
    const {validateQuestion = true} = options
    const status = await surveysApi.getSurveyStatus(survey.id)

    if (status === 3) {
      WbAlert({
        message: 'Survey is live, cannot add new question',
        type: 'error',
      })
      navigate(paths.dashboard)
      return
    }

    if (status == 1 || status == 2) {
      if (validateQuestion && !validate(i)) {
        return
      }
      await saveQuestionAtIndex(i, options)
    }
  }

  const saveContentScreenAtIndex = async i => {
    const entry = questions[i]
    const errors = validateContentScreen(entry.content)
    setContentErrors(errors)
    if (errors.length > 0) return

    const status = Number(await surveysApi.getSurveyStatus(survey.id))
    if (status === 3) {
      WbAlert({
        message: 'Survey is live, cannot change content screens',
        type: 'error',
      })
      navigate(paths.dashboard)
      return
    }
    if (status !== 1 && status !== 2) return

    const content = normalizeContentScreen(entry.content)
    const saved = entry.itemId
      ? await surveyItemApi.updateContentScreen({
          surveyId: survey.id,
          itemId: entry.itemId,
          content,
        })
      : await surveyItemApi.addContentScreen({
          surveyId: survey.id,
          order: i + 1,
          content,
        })
    const qq = [...questions]
    qq[i] = {
      ...entry,
      itemType: ITEM_TYPE.CONTENT,
      itemId: saved?.id ?? entry.itemId,
      content: saved?.content ?? content,
    }
    onQuestionChange(qq)
    WbAlert({message: 'Content screen saved', type: 'success'})
  }

  const saveSkipLogicSectionAtIndex = async i => {
    const entry = questions[i]
    const errors = validateRules(
      {items: questions},
      entry.itemId,
      entry.rules,
      i
    )
    setSkipErrors(errors)
    if (errors.length > 0) return

    const status = Number(await surveysApi.getSurveyStatus(survey.id))
    if (status === 3) {
      WbAlert({
        message: 'Survey is live, cannot change skip logic',
        type: 'error',
      })
      navigate(paths.dashboard)
      return
    }
    if (status !== 1 && status !== 2) return

    const rules = entry.rules
    const saved = entry.itemId
      ? await surveyItemApi.updateSkipLogicSection({
          surveyId: survey.id,
          itemId: entry.itemId,
          rules,
        })
      : await surveyItemApi.addSkipLogicSection({
          surveyId: survey.id,
          order: i + 1,
          rules,
        })
    const qq = [...questions]
    qq[i] = {
      ...entry,
      itemType: ITEM_TYPE.SKIP_LOGIC,
      itemId: saved?.id ?? entry.itemId,
      rules: saved?.rules ?? rules,
    }
    onQuestionChange(qq)
    WbAlert({message: 'Skip logic saved', type: 'success'})
  }

  const deleteContentScreenAtIndex = async i => {
    const entry = questions[i]
    setPendingDelete(null)
    try {
      if (entry.itemId) {
        await surveyItemApi.deleteContentScreen(survey.id, entry.itemId)
      }
      onQuestionChange(questions.filter((x, idx) => idx !== i))
      setIndex(null)
      WbAlert({message: 'Content screen deleted', type: 'success'})
    } catch (err) {
      const msg = err?.message || err?.Message || ''
      if (msg.includes('Survey is Live')) {
        WbAlert({
          message: 'Survey is live, cannot delete content screen',
          type: 'error',
        })
        navigate(paths.dashboard)
        return
      }
      WbAlert({message: 'Error deleting content screen', type: 'error'})
    }
  }

  const requestContentScreenDelete = i => {
    setAnchorEl(null)
    const references = questions[i].itemId
      ? findReferences(toEngineSurvey(questions), questions[i].itemId)
      : []
    if (references.length > 0) {
      setPendingDelete({
        index: i,
        references,
        itemLabel: 'this content screen',
        onConfirm: () => deleteContentScreenAtIndex(i),
      })
    } else {
      deleteContentScreenAtIndex(i)
    }
  }

  const deleteSkipLogicSectionAtIndex = async i => {
    const entry = questions[i]
    setPendingDelete(null)
    try {
      if (entry.itemId) {
        await surveyItemApi.deleteSkipLogicSection(survey.id, entry.itemId)
      }
      onQuestionChange(questions.filter((x, idx) => idx !== i))
      setIndex(null)
      WbAlert({message: 'Skip-logic section deleted', type: 'success'})
    } catch (err) {
      const msg = err?.message || err?.Message || ''
      if (msg.includes('Survey is Live')) {
        WbAlert({
          message: 'Survey is live, cannot delete skip logic',
          type: 'error',
        })
        navigate(paths.dashboard)
        return
      }
      WbAlert({message: 'Error deleting skip-logic section', type: 'error'})
    }
  }

  const requestSkipLogicDelete = i => {
    setAnchorEl(null)
    const references = questions[i].itemId
      ? findReferences(toEngineSurvey(questions), questions[i].itemId)
      : []
    if (references.length > 0) {
      setPendingDelete({
        index: i,
        references,
        itemLabel: 'this skip-logic section',
        onConfirm: () => deleteSkipLogicSectionAtIndex(i),
      })
    } else {
      deleteSkipLogicSectionAtIndex(i)
    }
  }

  const sectionLabel = sectionKey =>
    `Skip logic at position ${questions.findIndex(x => x.itemId === sectionKey) + 1}`

  const updateQuestion = changes => {
    let qq = [...questions]
    qq[index] = {...changes}
    onQuestionChange([...qq])
  }

  useEffect(() => {
    setValue(priorityAudience ? Math.min(...priorityAudience) : 1)
  }, [index])

  const renderTabBar = i => {
    return (
      <Toolbar className="question-audience-tabs">
        <Tabs
          value={value}
          onChange={(e, v) => setValue(v)}
          centered
          className="custom-tabs"
          aria-label="preview-tab"
        >
          {AUDIENCE_SELECT.filter(x => priorityAudience?.includes(x.value)).map(
            (audience, index) => (
              <WbTab
                value={audience.value}
                label={audience.label}
                onClick={e => setValue(audience.value)}
              />
            )
          )}
        </Tabs>
        <Box
          sx={{
            justifyItems: 'flex-end',
            display: 'flex',
          }}
        >
          <IconButton
            onClick={e => setAnchorEl(e.currentTarget)}
            disableElevation
          >
            <MoreHoriz></MoreHoriz>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={e => setAnchorEl(null)}
          >
            <MenuItem
              onClick={e => {
                if (questions[i].id > 0) {
                  surveyQuestionApi
                    .delete(survey.id, questions[i].id)
                    .then(res => {
                      WbAlert({message: 'Question deleted', type: 'success'})
                      let qq = [...questions]
                      qq.splice(i, 1)
                      onQuestionChange([...qq])
                      setIndex(null)
                      setAnchorEl(null)
                    })
                    .catch(err => {
                      // The request wrapper rejects with a lowercase `message`.
                      // Reading `err.Message` threw here, so any delete error
                      // crashed the catch and the row was never removed (looked
                      // like "delete does nothing"). Read defensively.
                      const msg = err?.message || err?.Message || ''
                      if (msg.includes('Survey is Live')) {
                        WbAlert({
                          message: 'Survey is live, cannot delete question',
                          type: 'error',
                        })
                        navigate(paths.dashboard)
                        return
                      }
                      WbAlert({
                        message: 'Error deleting question',
                        type: 'error',
                      })
                    })
                } else {
                  onQuestionChange([...questions.filter((x, idx) => idx !== i)])
                  setIndex(null)
                  setAnchorEl(null)
                }
              }}
            >
              Delete
            </MenuItem>
          </Menu>
          <IconButton
            onClick={async () => {
              try {
                await refreshSurveyQuestionsByToken()
              } catch (e) {
                // If reload fails, keep existing UI behavior.
              }
              setIndex(null)
            }}
          >
            <Close></Close>
          </IconButton>
        </Box>
      </Toolbar>
    )
  }

  const validate = i => {
    let aud = survey.audience
    if (
      (aud === 1 || aud === 3 || aud === 5 || aud === 7) &&
      !questions[i].staffText
    ) {
      WbAlert({message: 'Question text for staff is required', type: 'error'})
      return false
    }
    if (
      (aud === 2 || aud === 3 || aud === 6 || aud === 7) &&
      !questions[i].parentText
    ) {
      WbAlert({message: 'Question text for parents is required', type: 'error'})
      return false
    }
    if (
      (aud === 4 || aud === 5 || aud === 6 || aud === 7) &&
      !questions[i].pupilText
    ) {
      WbAlert({
        message: 'Question text for studnets is required',
        type: 'error',
      })
      return false
    }

    return true
  }
  const renderAddButton = i => {
    return (
      <WbOutlineButton
        onClick={async e => {
          try {
            await saveQuestionWithStatusCheck(i, {showSuccessAlert: true})
          } catch (err) {
            WbAlert({
              message: 'Error saving question',
              type: 'error',
            })
          }
        }}
        CustomOutlineButtonText={'Save'}
        sx={{marginTop: '15px'}}
      ></WbOutlineButton>
    )
  }
  const renderForm = i => {
    if (isContentScreen(questions[i])) return renderContentForm(i)
    if (isSkipLogicSection(questions[i])) return renderSkipLogicForm(i)
    return (
      <>
        {renderTabBar(i)}
        {AUDIENCE_SELECT.filter(x => priorityAudience.includes(x.value)).map(
          (audience, index) => (
            <WbTabPanel value={value} index={audience.value}>
              <div className="questions">
                <Grid container>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    {getFormType(questions[i].questionType, i, audience.value)}
                  </Grid>
                  <Grid item lg={12} textAlign="right">
                    {questions[i] && renderAddButton(i)}
                  </Grid>
                </Grid>
              </div>
            </WbTabPanel>
          )
        )}
      </>
    )
  }
  const renderContentForm = i => (
    <>
      <Toolbar
        className="question-audience-tabs"
        sx={{justifyContent: 'space-between'}}
      >
        <strong>Content screen</strong>
        <Box sx={{display: 'flex'}}>
          <IconButton
            aria-label="More options"
            onClick={e => setAnchorEl(e.currentTarget)}
          >
            <MoreHoriz></MoreHoriz>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={e => setAnchorEl(null)}
          >
            <MenuItem onClick={() => requestContentScreenDelete(i)}>
              Delete
            </MenuItem>
          </Menu>
          <IconButton
            aria-label="Close"
            onClick={async () => {
              try {
                await refreshSurveyQuestionsByToken()
              } catch (e) {
                // If reload fails, keep existing UI behavior.
              }
              setContentErrors([])
              setIndex(null)
            }}
          >
            <Close></Close>
          </IconButton>
        </Box>
      </Toolbar>
      <div className="questions">
        <ContentScreenEditor
          content={questions[i].content}
          errors={contentErrors}
          onChange={content => updateQuestion({...questions[i], content})}
        />
        <Box textAlign="right">
          <WbOutlineButton
            onClick={async () => {
              try {
                await saveContentScreenAtIndex(i)
              } catch (err) {
                WbAlert({
                  message: 'Error saving content screen',
                  type: 'error',
                })
              }
            }}
            CustomOutlineButtonText={'Save'}
            sx={{marginTop: '15px'}}
          ></WbOutlineButton>
        </Box>
      </div>
    </>
  )
  const renderSkipLogicForm = i => (
    <>
      <Toolbar
        className="question-audience-tabs"
        sx={{justifyContent: 'space-between'}}
      >
        <strong>Skip logic</strong>
        <Box sx={{display: 'flex'}}>
          <IconButton
            aria-label="More options"
            onClick={e => setAnchorEl(e.currentTarget)}
          >
            <MoreHoriz></MoreHoriz>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={e => setAnchorEl(null)}
          >
            <MenuItem onClick={() => requestSkipLogicDelete(i)}>Delete</MenuItem>
          </Menu>
          <IconButton
            aria-label="Close"
            onClick={async () => {
              try {
                await refreshSurveyQuestionsByToken()
              } catch (e) {
                // If reload fails, keep existing UI behavior.
              }
              setSkipErrors([])
              setIndex(null)
            }}
          >
            <Close></Close>
          </IconButton>
        </Box>
      </Toolbar>
      <div className="questions">
        <SkipLogicSectionEditor
          rules={questions[i].rules}
          survey={{items: questions}}
          sectionId={questions[i].itemId}
          sectionIndex={i}
          errors={skipErrors}
          onChange={rules => updateQuestion({...questions[i], rules})}
        />
        <Box textAlign="right">
          <WbOutlineButton
            onClick={async () => {
              try {
                await saveSkipLogicSectionAtIndex(i)
              } catch (err) {
                WbAlert({message: 'Error saving skip logic', type: 'error'})
              }
            }}
            CustomOutlineButtonText={'Save'}
            sx={{marginTop: '15px'}}
          ></WbOutlineButton>
        </Box>
      </div>
    </>
  )
  const getFormType = (t, i, tab) => {
    switch (t) {
      case QUESTION_TYPE.NUMBER_SLIDER:
      case QUESTION_TYPE.PERCENTAGE:
        return (
          <NumberSlider
            survey={survey}
            question={{...questions[i]}}
            updateQuestion={updateQuestion}
            onAutoSaveCategory={category =>
              saveQuestionWithStatusCheck(i, {
                showSuccessAlert: false,
                categoryOverride: category,
              })
            }
            audience={tab}
            isPriority={Math.min(...priorityAudience) === tab}
          ></NumberSlider>
        )

      case QUESTION_TYPE.MULTIPLE_SELECT:
        return (
          <MultipleSelect
            survey={survey}
            question={{...questions[i]}}
            updateQuestion={updateQuestion}
            onAutoSaveCategory={category =>
              saveQuestionWithStatusCheck(i, {
                showSuccessAlert: false,
                categoryOverride: category,
              })
            }
            audience={tab}
            isPriority={Math.min(...priorityAudience) === tab}
          ></MultipleSelect>
        )
      case QUESTION_TYPE.YES_NO:
      case QUESTION_TYPE.YES_NO_MAYBE:
      case QUESTION_TYPE.LIKERT:
      case QUESTION_TYPE.MULTIPLE_CHOICE:
        return (
          <MultipleOption
            survey={survey}
            question={{...questions[i]}}
            updateQuestion={updateQuestion}
            onAutoSaveCategory={category =>
              saveQuestionWithStatusCheck(i, {
                showSuccessAlert: false,
                categoryOverride: category,
              })
            }
            audience={tab}
            isPriority={Math.min(...priorityAudience) === tab}
          ></MultipleOption>
        )

      case QUESTION_TYPE.RANKING:
        return (
          <RankingQuestion
            survey={survey}
            question={{...questions[i]}}
            updateQuestion={updateQuestion}
            onAutoSaveCategory={category =>
              saveQuestionWithStatusCheck(i, {
                showSuccessAlert: false,
                categoryOverride: category,
              })
            }
            audience={tab}
            isPriority={Math.min(...priorityAudience) === tab}
          />
        )
      case QUESTION_TYPE.COMMENTS:
        return (
          <TextQuestion
            survey={survey}
            question={{...questions[i]}}
            updateQuestion={updateQuestion}
            onAutoSaveCategory={category =>
              saveQuestionWithStatusCheck(i, {
                showSuccessAlert: false,
                categoryOverride: category,
              })
            }
            audience={tab}
            isPriority={Math.min(...priorityAudience) === tab}
          ></TextQuestion>
        )
      default:
        return 'template to create ' + t
    }
  }
  const onReorder = (from, to) => {
    let qq = [...questions]
    let temp = qq[from]
    qq = qq.filter((x, i) => i !== from)
    var q1 = qq.slice(0, to)
    var q2 = qq.slice(to)
    qq = [...q1, temp, ...q2]
    qq = qq.map((x, i) => {
      return {...x, order: i + 1}
    })
    onQuestionChange([...qq])
    // A mixed list (any content screen or skip-logic section) reorders by item
    // id through the item endpoint; a question-only survey keeps the legacy
    // question-id reorder.
    if (qq.some(x => (isContentScreen(x) || isSkipLogicSection(x)) && x.itemId != null)) {
      if (temp.itemId != null) {
        surveyItemApi.reorder(survey.id, temp.itemId, to)
      }
    } else if (!isContentScreen(temp)) {
      surveyQuestionApi.reorderSurveyQuestions(
        temp.id,
        qq[to].dsid,
        from,
        to,
        survey.id
      )
    }
    // let qq = [...questions]
    // let temp = qq[from]
    // qq[from] = qq[to]
    // qq[from].order = from
    // qq[to] = temp
    // qq[to].order = to
    // onQuestionChange([...qq])
    //surveyQuestionApi.reorderSurveyQuestions(
    //   qq[from].id,
    //   qq[to].dsid,
    //   from,
    //   to,
    //   survey.id
    // )
  }
  const renderSingleQuestion = i => {
    if (isContentScreen(questions[i])) {
      return (
        <QuestionRow
          index={i}
          onReorder={onReorder}
          typeLabel="Content screen"
          onChange={v => {
            setContentErrors([])
            setIndex(v)
          }}
          text={contentScreenTitle(questions[i].content)}
        />
      )
    }
    if (isSkipLogicSection(questions[i])) {
      return (
        <QuestionRow
          index={i}
          onReorder={onReorder}
          typeLabel="Skip logic"
          onChange={v => {
            setSkipErrors([])
            setIndex(v)
          }}
          text="Not shown to respondents"
        />
      )
    }
    let p = Math.min(...priorityAudience)
    return (
      <QuestionRow
        index={i}
        onReorder={onReorder}
        categoryName={questions[i].categoryName}
        onChange={v => {
          setIndex(v)
          setValue(p)
        }}
        text={
          p === 1
            ? questions[i].staffText
            : p === 4
              ? questions[i].pupilText
              : questions[i].parentText
        }
      />
    )
  }

  return (
    <>
      {questions?.length === 0 ? (
        <h3 className="sub-heading">Let's start building your survey!</h3>
      ) : (
        <>
          {questions?.map((question, i) => (
            <div key={i} value={i} className="create-question">
              {i === index ? renderForm(i) : renderSingleQuestion(i)}
            </div>
          ))}
        </>
      )}
      {pendingDelete && (
        <ItemDeleteGuard
          itemLabel={pendingDelete.itemLabel}
          references={pendingDelete.references}
          sectionLabel={sectionLabel}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => pendingDelete.onConfirm()}
        />
      )}
    </>
  )
}
